import { type ChangePiece, TextEditPlanner } from "@common/utils/redline/text-edit-planner";
import { wordDiff } from "@common/utils/redline/text/word-diff";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

export type EditableTextOrigin =
    | { kind: "netNew"; engineText: string }
    | { kind: "revised"; documentText: string; engineText: string }
    | { kind: "untouched"; documentText: string };

export type EditableTokenAuthor = "engine" | "user";

interface EditableTokenFields {
    text: string;
    trailingSeparator: string;
    author: EditableTokenAuthor | null;
    withinWord: boolean;
}

export interface EditableKeptToken extends EditableTokenFields {
    kind: "kept";
}

export interface EditableInsertedToken extends EditableTokenFields {
    kind: "inserted";
}

export interface EditableStruckToken extends EditableTokenFields {
    kind: "deleted";
    sourceSeparatorBefore: string;
    sourceSeparatorAfter: string;
    attachesToPreviousWord?: true;
}

export type EditableToken = EditableKeptToken | EditableInsertedToken | EditableStruckToken;

export const DELETED_TOKEN_ATTRIBUTE = "data-redline-deleted";
export const RESTORE_TEXT_ATTRIBUTE = "data-restore-text";
export const RESTORE_SEPARATOR_BEFORE_ATTRIBUTE = "data-restore-separator-before";
export const RESTORE_SEPARATOR_AFTER_ATTRIBUTE = "data-restore-separator-after";
export const RESTORE_ATTACHES_TO_PREVIOUS_WORD_ATTRIBUTE = "data-restore-attaches-to-previous-word";
export const DELETED_TOKEN_TITLE = "Click to restore this text";

interface TextUnit {
    text: string;
}

interface UnitWithSourceSeparators extends TextUnit {
    startOffset: number;
    endOffset: number;
    sourceSeparatorBefore: string;
    sourceSeparatorAfter: string;
    attachesToPreviousWord?: true;
}

interface UnitChangePiece {
    kind: "kept" | "deleted" | "inserted";
    text: string;
}

interface UnitChangedInPlace {
    documentUnit: UnitWithSourceSeparators;
    pieces: UnitChangePiece[];
}

interface EngineCardWord extends UnitWithSourceSeparators {
    kind: "kept" | "inserted";
    changedInPlace: UnitChangedInPlace | null;
    keptDocumentTail: UnitWithSourceSeparators | null;
}

interface EngineReading {
    cardWords: EngineCardWord[];
    deletedBeforeSlots: UnitWithSourceSeparators[][];
}

interface CardWordPiece extends UnitChangePiece {
    pieceIndex: number;
    documentUnit: UnitWithSourceSeparators | null;
    sourceStartOffset: number;
    sourceEndOffset: number;
}

interface TextPart {
    startOffset: number;
    endOffset: number;
}

function comparisonKeysOf(units: TextUnit[]): string[] {
    return units.map((unit) => WordTokenizer.comparisonKey(unit.text));
}

function unitsWithSourceSeparatorsOf(text: string): UnitWithSourceSeparators[] {
    const units = WordTokenizer.diffUnitsWithTrailingSeparators(text);
    return units.map((unit, unitIndex) => ({
        text: unit.text,
        startOffset: unit.startOffset,
        endOffset: unit.endOffset,
        sourceSeparatorBefore: unitIndex > 0 ? units[unitIndex - 1].trailingSeparator : "",
        sourceSeparatorAfter: unitIndex < units.length - 1 ? unit.trailingSeparator : "",
    }));
}

function unitsOverlapping(units: UnitWithSourceSeparators[], startOffset: number, endOffset: number): number[] {
    const indexes: number[] = [];
    units.forEach((unit, unitIndex) => {
        if (unit.startOffset < endOffset && unit.endOffset > startOffset) indexes.push(unitIndex);
    });
    return indexes;
}

function unitIndexHolding(units: UnitWithSourceSeparators[], offset: number): number {
    return units.findIndex((unit) => unit.startOffset <= offset && offset < unit.endOffset);
}

function firstUnitIndexStartingAtOrAfter(units: UnitWithSourceSeparators[], offset: number): number {
    const unitIndex = units.findIndex((unit) => unit.startOffset >= offset);
    return unitIndex < 0 ? units.length : unitIndex;
}

function nonWhitespacePartsOf(text: string): TextPart[] {
    return WordTokenizer.positioned(text).map((word) => ({ startOffset: word.startOffset, endOffset: word.endOffset }));
}

function keptRemainderCardWordIndex(documentUnit: UnitWithSourceSeparators, keptPieces: ChangePiece[], engineUnits: UnitWithSourceSeparators[]): number {
    const keptPieceEndingInsideUnit = keptPieces.find((piece) => piece.sourceEndOffset > documentUnit.startOffset && piece.sourceEndOffset <= documentUnit.endOffset && piece.sourceStartOffset < documentUnit.endOffset);
    if (keptPieceEndingInsideUnit !== undefined && keptPieceEndingInsideUnit.requestEndOffset > 0) return unitIndexHolding(engineUnits, keptPieceEndingInsideUnit.requestEndOffset - 1);
    const keptPieceStartingInsideUnit = keptPieces.find((piece) => piece.sourceStartOffset >= documentUnit.startOffset && piece.sourceStartOffset < documentUnit.endOffset);
    if (keptPieceStartingInsideUnit !== undefined) return unitIndexHolding(engineUnits, keptPieceStartingInsideUnit.requestStartOffset);
    return -1;
}

function engineReadingOf(documentText: string, engineText: string): EngineReading {
    const documentUnits = unitsWithSourceSeparatorsOf(documentText);
    const engineUnits = unitsWithSourceSeparatorsOf(engineText);
    const pieces = TextEditPlanner.piecesBetween(documentText, engineText);
    const keptPieces = pieces.filter((piece) => piece.kind === "kept");
    const piecesOfCardWord: CardWordPiece[][] = engineUnits.map(() => []);
    const deletedBeforeSlots: UnitWithSourceSeparators[][] = [...engineUnits.map((): UnitWithSourceSeparators[] => []), []];

    const distributeDeletedPiece = (piece: ChangePiece, pieceIndex: number): void => {
        const parts = nonWhitespacePartsOf(piece.text);
        parts.forEach((part, partIndex) => {
            const sourceStartOffset = piece.sourceStartOffset + part.startOffset;
            const sourceEndOffset = piece.sourceStartOffset + part.endOffset;
            const documentUnitIndex = unitIndexHolding(documentUnits, sourceStartOffset);
            const documentUnit = documentUnitIndex < 0 ? null : documentUnits[documentUnitIndex];
            const deletesTheWholeUnit = documentUnit !== null && documentUnit.startOffset === sourceStartOffset && documentUnit.endOffset === sourceEndOffset;
            const cardWordIndex = documentUnit === null || deletesTheWholeUnit ? -1 : keptRemainderCardWordIndex(documentUnit, keptPieces, engineUnits);
            if (documentUnit === null || cardWordIndex < 0) {
                deletedBeforeSlots[firstUnitIndexStartingAtOrAfter(engineUnits, piece.requestStartOffset)].push(documentUnit ?? { text: piece.text.slice(part.startOffset, part.endOffset), startOffset: sourceStartOffset, endOffset: sourceEndOffset, sourceSeparatorBefore: "", sourceSeparatorAfter: "" });
                return;
            }
            const leadingWhitespace = partIndex === 0 ? piece.text.slice(0, part.startOffset) : "";
            piecesOfCardWord[cardWordIndex].push({
                kind: "deleted",
                text: leadingWhitespace + piece.text.slice(part.startOffset, part.endOffset),
                pieceIndex,
                documentUnit,
                sourceStartOffset: sourceStartOffset - leadingWhitespace.length,
                sourceEndOffset,
            });
        });
    };

    const distributeKeptOrInsertedPiece = (piece: ChangePiece, pieceIndex: number): void => {
        const engineUnitIndexes = unitsOverlapping(engineUnits, piece.requestStartOffset, piece.requestEndOffset);
        const documentUnitIndexes = piece.kind === "kept" ? unitsOverlapping(documentUnits, piece.sourceStartOffset, piece.sourceEndOffset) : [];
        engineUnitIndexes.forEach((engineUnitIndex, position) => {
            const engineUnit = engineUnits[engineUnitIndex];
            const clipStart = Math.max(piece.requestStartOffset, engineUnit.startOffset);
            const clipEnd = Math.min(piece.requestEndOffset, engineUnit.endOffset);
            const documentUnitIndex = documentUnitIndexes[position];
            const documentUnit = documentUnitIndex === undefined ? null : documentUnits[documentUnitIndex];
            const sourceStartOffset = documentUnit === null || clipStart > engineUnit.startOffset ? piece.sourceStartOffset : documentUnit.startOffset;
            const sourceEndOffset = documentUnit === null || clipEnd < engineUnit.endOffset ? piece.sourceEndOffset : documentUnit.endOffset;
            piecesOfCardWord[engineUnitIndex].push({ kind: piece.kind, text: engineText.slice(clipStart, clipEnd), pieceIndex, documentUnit, sourceStartOffset, sourceEndOffset });
        });
    };

    pieces.forEach((piece, pieceIndex) => {
        if (piece.kind === "deleted") distributeDeletedPiece(piece, pieceIndex);
        else distributeKeptOrInsertedPiece(piece, pieceIndex);
    });

    const cardWords = engineUnits.map((engineUnit, engineUnitIndex): EngineCardWord => {
        const wordPieces = piecesOfCardWord[engineUnitIndex].sort((left, right) => left.pieceIndex - right.pieceIndex);
        const wholeWordKind = wordPieces.length === 1 && wordPieces[0].kind !== "deleted" && wordPieces[0].text === engineUnit.text ? wordPieces[0].kind : null;
        if (wholeWordKind !== null || wordPieces.length === 0) return { ...engineUnit, kind: wholeWordKind ?? "kept", changedInPlace: null, keptDocumentTail: null };
        const lastKeptPiece = [...wordPieces].reverse().find((piece) => piece.kind === "kept") ?? null;
        const keptDocumentTail: UnitWithSourceSeparators | null = wordPieces[0].kind === "inserted" && lastKeptPiece !== null && lastKeptPiece.documentUnit !== null
            ? {
                text: documentText.slice(lastKeptPiece.sourceStartOffset, lastKeptPiece.sourceEndOffset),
                startOffset: lastKeptPiece.sourceStartOffset,
                endOffset: lastKeptPiece.sourceEndOffset,
                sourceSeparatorBefore: "",
                sourceSeparatorAfter: lastKeptPiece.documentUnit.sourceSeparatorAfter,
                attachesToPreviousWord: true,
            }
            : null;
        const documentUnit = keptDocumentTail ?? wordPieces.find((piece) => piece.documentUnit !== null)?.documentUnit ?? engineUnit;
        return {
            ...engineUnit,
            kind: "inserted",
            changedInPlace: { documentUnit, pieces: wordPieces.map(({ kind, text }) => ({ kind, text })) },
            keptDocumentTail,
        };
    });
    return { cardWords, deletedBeforeSlots };
}

function plainToken(text: string, trailingSeparator: string): EditableKeptToken {
    return { kind: "kept", text, trailingSeparator, author: null, withinWord: false };
}

function struckUnitToken(unit: UnitWithSourceSeparators, author: EditableTokenAuthor): EditableStruckToken {
    const token: EditableStruckToken = { kind: "deleted", text: unit.text, trailingSeparator: "", author, withinWord: false, sourceSeparatorBefore: unit.sourceSeparatorBefore, sourceSeparatorAfter: unit.sourceSeparatorAfter };
    return unit.attachesToPreviousWord === true ? { ...token, attachesToPreviousWord: true } : token;
}

function engineCardWordToken(engineWord: EngineCardWord, userUnit: DiffUnitWithTrailingSeparator): EditableKeptToken | EditableInsertedToken {
    if (engineWord.kind === "kept") return plainToken(userUnit.text, userUnit.trailingSeparator);
    return { kind: "inserted", text: userUnit.text, trailingSeparator: userUnit.trailingSeparator, author: "engine", withinWord: false };
}

function unitChangedInPlaceTokens(unitChange: UnitChangedInPlace, userUnit: DiffUnitWithTrailingSeparator): EditableToken[] {
    const pieces = unitChange.pieces.map((piece): EditableToken => {
        if (piece.kind === "kept") return keptPiece(piece.text);
        if (piece.kind === "inserted") return { kind: "inserted", text: piece.text, trailingSeparator: "", author: "engine", withinWord: true };
        return { kind: "deleted", text: piece.text, trailingSeparator: "", author: "engine", withinWord: true, sourceSeparatorBefore: "", sourceSeparatorAfter: "" };
    });
    pieces[pieces.length - 1].trailingSeparator = userUnit.trailingSeparator;
    return pieces;
}

function keptPiece(text: string): EditableKeptToken {
    return { kind: "kept", text, trailingSeparator: "", author: null, withinWord: true };
}

function userInsertedToken(text: string, trailingSeparator: string, withinWord: boolean): EditableInsertedToken {
    return { kind: "inserted", text, trailingSeparator, author: "user", withinWord };
}

function struckPiece(text: string): EditableStruckToken {
    return { kind: "deleted", text, trailingSeparator: "", author: "user", withinWord: true, sourceSeparatorBefore: "", sourceSeparatorAfter: "" };
}

function documentWordReplacedTokens(documentWord: EngineCardWord, userWord: DiffUnitWithTrailingSeparator): EditableToken[] {
    const before = Array.from(documentWord.text);
    const after = Array.from(userWord.text);
    let prefixLength = 0;
    while (prefixLength < before.length && prefixLength < after.length && before[prefixLength] === after[prefixLength]) prefixLength += 1;
    let suffixLength = 0;
    while (suffixLength < before.length - prefixLength && suffixLength < after.length - prefixLength && before[before.length - 1 - suffixLength] === after[after.length - 1 - suffixLength]) suffixLength += 1;
    const wordsAreUnrelated = (prefixLength + suffixLength) * 2 < Math.max(before.length, after.length);
    if (wordsAreUnrelated) return [struckUnitToken(documentWord, "user"), userInsertedToken(userWord.text, userWord.trailingSeparator, false)];
    const removedMiddle = before.slice(prefixLength, before.length - suffixLength).join("");
    const addedMiddle = after.slice(prefixLength, after.length - suffixLength).join("");
    const pieces: EditableToken[] = [];
    if (prefixLength > 0) pieces.push(keptPiece(before.slice(0, prefixLength).join("")));
    if (removedMiddle.length > 0) pieces.push(struckPiece(removedMiddle));
    if (addedMiddle.length > 0) pieces.push(userInsertedToken(addedMiddle, "", true));
    if (suffixLength > 0) pieces.push(keptPiece(after.slice(after.length - suffixLength).join("")));
    pieces[pieces.length - 1].trailingSeparator = userWord.trailingSeparator;
    return pieces;
}

function separatorIsRendered(token: EditableToken): boolean {
    return token.kind !== "deleted" || token.withinWord;
}

function withTextEdgeWhitespace(tokens: EditableToken[], leadingWhitespace: string, trailingWhitespace: string): EditableToken[] {
    const opened = leadingWhitespace.length > 0 ? [plainToken("", leadingWhitespace), ...tokens] : tokens;
    if (trailingWhitespace.length === 0) return opened;
    const lastRenderedIndex = opened.map(separatorIsRendered).lastIndexOf(true);
    if (lastRenderedIndex < 0) return [...opened, plainToken("", trailingWhitespace)];
    return opened.map((token, index) => (index === lastRenderedIndex ? { ...token, trailingSeparator: token.trailingSeparator + trailingWhitespace } : token));
}

function userDroppedOnlyEngineWordsBeforeKeptTail(removed: EngineCardWord[], added: DiffUnitWithTrailingSeparator[]): boolean {
    const keptDocumentTail = removed[removed.length - 1]?.keptDocumentTail ?? null;
    if (keptDocumentTail === null || added.length !== 1 || removed.length < 2 || removed[0].kind !== "kept") return false;
    return removed.slice(1).every((cardWord) => cardWord.kind === "inserted") && added[0].text === removed[0].text + keptDocumentTail.text;
}

function revisedTokensOf(documentText: string, engineText: string, userUnits: DiffUnitWithTrailingSeparator[]): EditableToken[] {
    const reading = engineReadingOf(documentText, engineText);
    const slots = reading.deletedBeforeSlots.map((slot) => [...slot]);
    const hunks = wordDiff(comparisonKeysOf(reading.cardWords), comparisonKeysOf(userUnits));
    const tokens: EditableToken[] = [];

    const emitSlot = (slotIndex: number, added: DiffUnitWithTrailingSeparator[]) => {
        const deletedUnits = slots[slotIndex];
        slots[slotIndex] = [];
        const restoredDeletedIndexes = new Set<number>();
        const restoredDeletedIndexOf = added.map((word) => {
            const deletedIndex = deletedUnits.findIndex((deletedUnit, index) => WordTokenizer.comparisonKey(deletedUnit.text) === WordTokenizer.comparisonKey(word.text) && !restoredDeletedIndexes.has(index));
            if (deletedIndex >= 0) restoredDeletedIndexes.add(deletedIndex);
            return deletedIndex;
        });
        let nextStruckIndex = 0;
        const emitStruckBefore = (deletedIndexEnd: number) => {
            for (; nextStruckIndex < deletedIndexEnd; nextStruckIndex += 1) {
                if (!restoredDeletedIndexes.has(nextStruckIndex)) tokens.push(struckUnitToken(deletedUnits[nextStruckIndex], "engine"));
            }
        };
        added.forEach((word, addedIndex) => {
            emitStruckBefore(restoredDeletedIndexOf[addedIndex] < 0 ? deletedUnits.length : restoredDeletedIndexOf[addedIndex]);
            tokens.push(restoredDeletedIndexOf[addedIndex] < 0 ? userInsertedToken(word.text, word.trailingSeparator, false) : plainToken(word.text, word.trailingSeparator));
        });
        emitStruckBefore(deletedUnits.length);
    };

    const slotDocumentUnitOfChangedCardWord = (cardWordIndex: number) => {
        const cardWord = reading.cardWords[cardWordIndex];
        if (cardWord.changedInPlace !== null && cardWord.keptDocumentTail === null) slots[cardWordIndex].push(cardWord.changedInPlace.documentUnit);
    };

    const keptDocumentTailTheUserDropped = (cardWordIndex: number, userTextInItsPlace: string): UnitWithSourceSeparators | null => {
        const keptDocumentTail = reading.cardWords[cardWordIndex].keptDocumentTail;
        return keptDocumentTail !== null && !userTextInItsPlace.endsWith(keptDocumentTail.text) ? keptDocumentTail : null;
    };

    const strikeTailOnTheUsersLastWord = (keptDocumentTail: UnitWithSourceSeparators) => {
        const usersLastWord = tokens[tokens.length - 1];
        tokens[tokens.length - 1] = { ...usersLastWord, trailingSeparator: "" };
        tokens.push({ ...struckUnitToken(keptDocumentTail, "engine"), withinWord: true, trailingSeparator: usersLastWord.trailingSeparator });
    };

    for (const hunk of hunks) {
        const removed = reading.cardWords.slice(hunk.beforeStart, hunk.beforeEnd);
        const added = userUnits.slice(hunk.afterStart, hunk.afterEnd);
        if (hunk.tag === "equal") {
            removed.forEach((engineWord, offset) => {
                const userUnit = added[offset];
                const unitChange = engineWord.changedInPlace !== null && userUnit.text === engineWord.text ? engineWord.changedInPlace : null;
                if (unitChange === null) slotDocumentUnitOfChangedCardWord(hunk.beforeStart + offset);
                emitSlot(hunk.beforeStart + offset, []);
                tokens.push(...(unitChange === null ? [engineCardWordToken(engineWord, userUnit)] : unitChangedInPlaceTokens(unitChange, userUnit)));
                const droppedTail = unitChange === null ? keptDocumentTailTheUserDropped(hunk.beforeStart + offset, userUnit.text) : null;
                if (droppedTail !== null) strikeTailOnTheUsersLastWord(droppedTail);
            });
            continue;
        }
        if (hunk.tag === "insert") {
            emitSlot(hunk.beforeStart, added);
            continue;
        }
        if (hunk.tag === "replace" && removed.length === added.length) {
            removed.forEach((engineWord, offset) => {
                slotDocumentUnitOfChangedCardWord(hunk.beforeStart + offset);
                if (engineWord.kind === "inserted") emitSlot(hunk.beforeStart + offset, [added[offset]]);
                else {
                    emitSlot(hunk.beforeStart + offset, []);
                    tokens.push(...documentWordReplacedTokens(engineWord, added[offset]));
                }
                const droppedTail = keptDocumentTailTheUserDropped(hunk.beforeStart + offset, added[offset].text);
                if (droppedTail !== null) strikeTailOnTheUsersLastWord(droppedTail);
            });
            continue;
        }
        if (userDroppedOnlyEngineWordsBeforeKeptTail(removed, added)) {
            emitSlot(hunk.beforeStart, []);
            tokens.push(plainToken(added[0].text, added[0].trailingSeparator));
            for (let offset = 1; offset < removed.length; offset += 1) emitSlot(hunk.beforeStart + offset, []);
            continue;
        }
        const lastRemovedIndex = hunk.beforeEnd - 1;
        removed.forEach((engineWord, offset) => {
            slotDocumentUnitOfChangedCardWord(hunk.beforeStart + offset);
            emitSlot(hunk.beforeStart + offset, []);
            if (engineWord.kind === "kept") tokens.push(struckUnitToken(engineWord, "user"));
            if (hunk.beforeStart + offset !== lastRemovedIndex && engineWord.keptDocumentTail !== null) tokens.push(struckUnitToken(engineWord.keptDocumentTail, "engine"));
        });
        if (added.length > 0) emitSlot(hunk.beforeEnd, added);
        const droppedTail = removed.length > 0 ? keptDocumentTailTheUserDropped(lastRemovedIndex, added[added.length - 1]?.text ?? "") : null;
        if (droppedTail === null) continue;
        if (added.length > 0) strikeTailOnTheUsersLastWord(droppedTail);
        else tokens.push(struckUnitToken(droppedTail, "engine"));
    }
    emitSlot(reading.cardWords.length, []);
    return tokens;
}

export function editableTokensOf(origin: EditableTextOrigin, textProposed: string): EditableToken[] {
    const userUnits = WordTokenizer.diffUnitsWithTrailingSeparators(textProposed);
    const firstUnit = userUnits[0];
    const lastUnit = userUnits[userUnits.length - 1];
    const leadingWhitespace = firstUnit === undefined ? "" : textProposed.slice(0, firstUnit.startOffset);
    const trailingWhitespace = lastUnit === undefined ? textProposed : textProposed.slice(lastUnit.endOffset);
    const unitTokens = origin.kind === "netNew"
        ? userUnits.map((unit) => plainToken(unit.text, unit.trailingSeparator))
        : revisedTokensOf(origin.documentText, origin.kind === "revised" ? origin.engineText : origin.documentText, userUnits);
    return withTextEdgeWhitespace(unitTokens, leadingWhitespace, trailingWhitespace);
}

export function restoreAttributesOf(token: EditableStruckToken): Record<string, string> {
    const attributes = {
        [RESTORE_TEXT_ATTRIBUTE]: token.text,
        [RESTORE_SEPARATOR_BEFORE_ATTRIBUTE]: token.sourceSeparatorBefore,
        [RESTORE_SEPARATOR_AFTER_ATTRIBUTE]: token.sourceSeparatorAfter,
    };
    return token.attachesToPreviousWord === true ? { ...attributes, [RESTORE_ATTACHES_TO_PREVIOUS_WORD_ATTRIBUTE]: "true" } : attributes;
}

interface RenderedTextPiece {
    text: string;
    isLineBreakElement: boolean;
}

export function editedTextOf(root: Node): string {
    const pieces = renderedTextPiecesOf(root).filter((piece) => piece.text.length > 0);
    const endsWithPlaceholderLineBreak = pieces.length > 0 && pieces[pieces.length - 1].isLineBreakElement;
    return (endsWithPlaceholderLineBreak ? pieces.slice(0, -1) : pieces).map((piece) => piece.text).join("");
}

function renderedTextPiecesOf(root: Node): RenderedTextPiece[] {
    const pieces: RenderedTextPiece[] = [];
    root.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            pieces.push({ text: node.textContent ?? "", isLineBreakElement: false });
            return;
        }
        if (!(node instanceof HTMLElement)) return;
        if (node.getAttribute(DELETED_TOKEN_ATTRIBUTE) === "true") return;
        if (node.tagName === "BR") {
            pieces.push({ text: "\n", isLineBreakElement: true });
            return;
        }
        pieces.push(...renderedTextPiecesOf(node));
    });
    return pieces;
}

export function caretOffsetIn(root: Node): number | null {
    const selection = root.ownerDocument?.getSelection() ?? null;
    if (selection === null || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const measured = root.ownerDocument.createRange();
    measured.setStart(root, 0);
    measured.setEnd(range.startContainer, range.startOffset);
    return renderedTextPiecesOf(measured.cloneContents()).reduce((length, piece) => length + piece.text.length, 0);
}

export function placeCaretAt(root: Node, offset: number): void {
    const document = root.ownerDocument;
    const selection = document?.getSelection() ?? null;
    if (document === null || selection === null) return;
    let remaining = offset;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (deletedTokenElementOf(node.parentNode) === null ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    let lastTextNode: Text | null = null;
    for (let textNode = walker.nextNode() as Text | null; textNode !== null; textNode = walker.nextNode() as Text | null) {
        lastTextNode = textNode;
        const length = textNode.textContent?.length ?? 0;
        if (remaining <= length) {
            const range = document.createRange();
            range.setStart(textNode, remaining);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            return;
        }
        remaining -= length;
    }
    const range = document.createRange();
    if (lastTextNode !== null) range.setStart(lastTextNode, lastTextNode.textContent?.length ?? 0);
    else range.setStart(root, root.childNodes.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}

function renderedTextOfSibling(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (node instanceof HTMLElement && node.getAttribute(DELETED_TOKEN_ATTRIBUTE) === "true") return "";
    return node.textContent ?? "";
}

function renderedTextBefore(element: HTMLElement): string {
    let text = "";
    for (let sibling = element.previousSibling; sibling !== null && text.length === 0; sibling = sibling.previousSibling) text = renderedTextOfSibling(sibling);
    return text;
}

function renderedTextAfter(element: HTMLElement): string {
    let text = "";
    for (let sibling = element.nextSibling; sibling !== null && text.length === 0; sibling = sibling.nextSibling) text = renderedTextOfSibling(sibling);
    return text;
}

function restoreAttachedToPreviousWord(deletedElement: HTMLElement, struckText: string): void {
    const previousNode = deletedElement.previousSibling;
    const previousText = previousNode?.nodeType === Node.TEXT_NODE ? (previousNode.textContent ?? "") : "";
    const whitespaceBefore = /\s*$/.exec(previousText)?.[0] ?? "";
    if (previousNode !== null && whitespaceBefore.length > 0) previousNode.textContent = previousText.slice(0, previousText.length - whitespaceBefore.length);
    const textAfterStartsWithWord = /^\S/.test(renderedTextAfter(deletedElement));
    const separatorAfter = textAfterStartsWithWord ? (deletedElement.getAttribute(RESTORE_SEPARATOR_AFTER_ATTRIBUTE) ?? "") : "";
    deletedElement.replaceWith(document.createTextNode(`${struckText}${separatorAfter}`));
}

export function restoreDeletedToken(deletedElement: HTMLElement): void {
    const struckText = deletedElement.getAttribute(RESTORE_TEXT_ATTRIBUTE) ?? "";
    if (deletedElement.getAttribute(RESTORE_ATTACHES_TO_PREVIOUS_WORD_ATTRIBUTE) === "true") {
        restoreAttachedToPreviousWord(deletedElement, struckText);
        return;
    }
    const textBeforeEndsInWord = /\S$/.test(renderedTextBefore(deletedElement));
    const textAfterStartsWithWord = /^\S/.test(renderedTextAfter(deletedElement));
    const separatorBefore = textBeforeEndsInWord ? (deletedElement.getAttribute(RESTORE_SEPARATOR_BEFORE_ATTRIBUTE) ?? "") : "";
    const separatorAfter = textAfterStartsWithWord ? (deletedElement.getAttribute(RESTORE_SEPARATOR_AFTER_ATTRIBUTE) ?? "") : "";
    deletedElement.replaceWith(document.createTextNode(`${separatorBefore}${struckText}${separatorAfter}`));
}

export function deletedTokenElementOf(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    const deletedElement = target.closest(`[${DELETED_TOKEN_ATTRIBUTE}="true"]`);
    return deletedElement instanceof HTMLElement ? deletedElement : null;
}
